// E-mail que chega pela URL e preenche o passo 1 do /register (FT-3290). Hoje quem manda é o auth
// novo (auth-front): no login com o Google sem conta na Jusfy, ele redireciona para o cadastro com
// `?login_hint=<e-mail>` (já verificado pelo Google). `?email=` é aceito pelo mesmo caminho, sem
// emissor conhecido. O campo continua livre: é só o valor inicial.
// Mesmas regras do jusfy-checkout (src/checkout/emailPrefill.ts).
//
// Caminho: um script inline do index.html, que roda antes do Segment (analytics.page()) e do GTM,
// guarda a query de chegada na aba (ARRIVAL_KEY) e tira os dois parâmetros da barra. O passo 1
// (useEmailPrefill) lê essa chegada na montagem.

export const EMAIL_PREFILL_PARAMS = ['login_hint', 'email'] as const;
export type EmailPrefillSource = (typeof EMAIL_PREFILL_PARAMS)[number];
export type EmailPrefill = { email: string; source: EmailPrefillSource };

/** Mesma regra do passo 1 (Cadastro/index.js usa esta constante). */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isSource = (value: unknown): value is EmailPrefillSource =>
  EMAIL_PREFILL_PARAMS.includes(value as EmailPrefillSource);

/**
 * Primeiro parâmetro com e-mail válido, na ordem de EMAIL_PREFILL_PARAMS (`login_hint` vence).
 * Um `+` sem codificação é o próprio `+` do e-mail (o auth codifica, mas um link escrito à mão
 * não): ele é protegido antes do parse, que o leria como espaço. Espaço que vem codificado
 * (`%20`) continua espaço, e e-mail com espaço não passa na validação.
 */
export function readEmailPrefill(search: string): EmailPrefill | null {
  const params = new URLSearchParams(search.replace(/\+/g, '%2B'));
  for (const source of EMAIL_PREFILL_PARAMS) {
    const raw = params.get(source);
    if (raw) {
      const email = raw.trim().toLowerCase();
      if (EMAIL_REGEX.test(email)) return { email, source };
    }
  }
  return null;
}

/**
 * Query sem os parâmetros de e-mail (`?a=1&b=2`, ou '' se não sobrar nada), ou null se não havia
 * nenhum. Corta o texto em vez de remontar por URLSearchParams: o resto da query chega igual ao que
 * veio (sem trocar `%20` por `+` nem `?flag` por `flag=`). Mesma regra do script do index.html;
 * aqui cobre a navegação dentro do app, que não passa por ele.
 */
export function stripEmailParams(search: string): string | null {
  const pairs = search.replace(/^\?/, '').split('&').filter(Boolean);
  const keyOf = (pair: string): string => {
    const key = pair.split('=')[0];
    try {
      return decodeURIComponent(key.replace(/\+/g, ' '));
    } catch {
      return key;
    }
  };
  const kept = pairs.filter(pair => !isSource(keyOf(pair)));
  if (kept.length === pairs.length) return null;
  return kept.length ? `?${kept.join('&')}` : '';
}

/**
 * Propriedades do Segment: de onde veio o e-mail preenchido e, para `login_hint`, a origem do
 * checkout (o auth). Ficam como na chegada, mesmo que a pessoa troque o e-mail depois.
 */
export const emailPrefillProps = (
  source: EmailPrefillSource | null,
): Record<string, string> =>
  source
    ? { email_prefill: source, ...(source === 'login_hint' ? { checkout_origin: 'auth' } : {}) }
    : {};

/** Query de chegada guardada pelo index.html (o mesmo nome está lá). */
export const ARRIVAL_KEY = 'jusfy_register_arrival';
/** E-mail aplicado e sua origem, para o recarregar e os eventos dos passos seguintes. */
export const EMAIL_PREFILL_STORAGE_KEY = 'jusfy_checkout_email_prefill';
export const EMAIL_PREFILL_TTL_MS = 30 * 60_000;

const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
const read = (key: string): string | null => {
  try {
    return session()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null): void => {
  try {
    if (value === null) session()?.removeItem(key);
    else session()?.setItem(key, value);
  } catch {
    /* sem armazenamento: segue sem lembrar */
  }
};

/** E-mail da chegada (guardada pelo index.html), sem consumi-la. */
export const readArrivalEmailPrefill = (): EmailPrefill | null => {
  const search = read(ARRIVAL_KEY);
  return search ? readEmailPrefill(search) : null;
};
export const clearArrival = (): void => write(ARRIVAL_KEY, null);

export function rememberEmailPrefill(prefill: EmailPrefill, now = Date.now()): void {
  write(EMAIL_PREFILL_STORAGE_KEY, JSON.stringify({ ...prefill, savedAt: now }));
}

export function recallEmailPrefill(now = Date.now()): EmailPrefill | null {
  try {
    const raw = read(EMAIL_PREFILL_STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (typeof saved?.savedAt !== 'number' || now - saved.savedAt > EMAIL_PREFILL_TTL_MS) return null;
    if (!isSource(saved.source) || typeof saved.email !== 'string' || !EMAIL_REGEX.test(saved.email)) return null;
    return { email: saved.email, source: saved.source };
  } catch {
    return null;
  }
}

const REGISTER_PATH = /^\/register(\/|$)/;

/**
 * Propriedades dos eventos do checkout, lidas na hora do evento. Só no /register: o /cadastro
 * (CadastroV2) usa os mesmos eventos e não preenche nada.
 */
export const currentEmailPrefillProps = (
  now = Date.now(),
  pathname = typeof window === 'undefined' ? '' : window.location.pathname,
): Record<string, string> =>
  REGISTER_PATH.test(pathname) ? emailPrefillProps(recallEmailPrefill(now)?.source ?? null) : {};
